import type { RouteRecordRaw, RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import useCartStore from './store';

function resetCompletedCart(to: RouteLocationNormalized, next: NavigationGuardNext): void {
  const routeCartId = to.params.id as string | undefined;
  const routeCheckoutStatus = to.meta.checkoutStatus as string | undefined;

  // NOTE :: If no route ID, allow navigation
  if (!routeCartId) return next();

  const store = useCartStore();
  const storeCartId = store.cartId;

  // NOTE :: Different ID - Not current cart (or store is empty): do nothing
  const differentId = routeCartId !== storeCartId || !storeCartId;
  if (differentId) return next();

  // NOTE :: Same ID - Current cart: trust route meta
  // If navigating to completed/pending route, reset store (handles stale 'active' status)
  if (routeCheckoutStatus === 'completed' || routeCheckoutStatus === 'pending') {
    next();

    const amountOfTimeToAvoidBlinkEmptyState = 50;

    setTimeout(() => {
      store.resetCart();
    }, amountOfTimeToAvoidBlinkEmptyState);

    return;
  }

  next();
}

export default <RouteRecordRaw[]>[
  // ACTIVE CART
  {
    path: 'animal/outcome/',
    name: 'animal-outcome',
    component: () => import('./views/CartView.vue'),
    meta: {
      checkoutStatus: 'active',
      noCancelRequests: true,
    },
  },
  {
    path: 'animal/outcome/:id',
    name: 'animal-outcome-with-id',
    component: () => import('./views/CartView.vue'),
    meta: {
      checkoutStatus: 'active',
      noCancelRequests: true,
    },
  },
  {
    path: 'animal/intake-payment/',
    name: 'animal-intake-payment',
    component: () => import('./views/CartView.vue'),
    meta: {
      checkoutStatus: 'active',
      noCancelRequests: true,
    },
  },
  {
    path: 'animal/intake-payment/:id',
    name: 'animal-intake-with-id',
    component: () => import('./views/CartView.vue'),
    meta: {
      checkoutStatus: 'active',
      noCancelRequests: true,
    },
  },
  {
    path: 'donation/payment/',
    name: 'donation-intake-payment',
    component: () => import('./views/CartView.vue'),
    meta: {
      checkoutStatus: 'active',
      noCancelRequests: true,
    },
  },
  {
    path: 'donation/payment/:id/:isAnonymous?',
    name: 'donation-with-id',
    component: () => import('./views/CartView.vue'),
    meta: {
      checkoutStatus: 'active',
      noCancelRequests: true,
    },
  },

  // PENDING PAYMENT CART
  {
    path: 'animal/outcome-pending/:id',
    name: 'animal-outcome-pending',
    component: () => import('./views/CartCheckoutPendingView.vue'),
    meta: {
      checkoutStatus: 'pending',
    },
    beforeEnter: (to, from, next) => resetCompletedCart(to, next),
  },
  {
    path: 'animal/intake-payment-pending/:id',
    name: 'animal-intake-payment-pending',
    component: () => import('./views/CartCheckoutPendingView.vue'),
    meta: {
      checkoutStatus: 'pending',
    },
    beforeEnter: (to, from, next) => resetCompletedCart(to, next),
  },
  {
    path: 'donation/payment-pending/:id',
    name: 'donation-payment-pending',
    component: () => import('./views/CartCheckoutPendingView.vue'),
    meta: {
      checkoutStatus: 'pending',
    },
    beforeEnter: (to, from, next) => resetCompletedCart(to, next),
  },

  // COMPLETED CART
  {
    path: 'animal/outcome-completed/:id',
    name: 'animal-outcome-completed',
    component: () => import('./views/CartCompletedOutcomeView.vue'),
    meta: {
      checkoutStatus: 'completed',
    },
    beforeEnter: (to, from, next) => resetCompletedCart(to, next),
  },
  {
    path: 'animal/intake-payment-completed/:id',
    name: 'animal-intake-payment-completed',
    component: () => import('./views/CartCompletedOutcomeView.vue'),
    meta: {
      checkoutStatus: 'completed',
    },
    beforeEnter: (to, from, next) => resetCompletedCart(to, next),
  },
  {
    path: 'donation/payment-completed/:id',
    name: 'donation-payment-completed',
    component: () => import('./views/CartCompletedOutcomeView.vue'),
    meta: {
      checkoutStatus: 'completed',
    },
    beforeEnter: (to, from, next) => resetCompletedCart(to, next),
  },
];
